import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "371",
  "slug": "ice-caps-glaciers-sea-ice-from-space",
  "title": "How to Tell Ice Caps, Glaciers, and Sea Ice Apart From Space",
  "excerpt": "Not all ice is the same. Glaciers, ice sheets, and sea ice form differently and look different from orbit. Here's a field guide to reading the planet's frozen places from satellite imagery.",
  "date": "2026-06-07",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1476249202953-93ec0d185ab7?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxnbGFjaWVyJTIwaWNlJTIwYWVyaWFsfGVufDF8MHx8fDE3ODA4NDU0Mjd8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Glaciers flow downhill on land, ice caps and ice sheets bury whole landscapes, and sea ice floats frozen on the ocean. Each looks different from above, so here's how to tell them apart.",
  "content": [
    {
      "type": "paragraph",
      "text": "From space, the frozen parts of the Earth can all look like the same blank white, but they are not. A glacier, an ice sheet, and a patch of sea ice form in completely different ways, behave differently, and matter to the planet for different reasons. Learning to tell them apart turns those white expanses from featureless blanks into a readable landscape, and it is one of the more satisfying things you can train your eye to do with satellite imagery."
    },
    {
      "type": "heading",
      "text": "Glaciers: Rivers of Ice"
    },
    {
      "type": "paragraph",
      "text": "A glacier is a thick mass of ice that forms on land where more snow falls each year than melts, slowly compacting into ice that then flows downhill under its own weight. From above, glaciers look like rivers of ice, because that is essentially what they are: they pour down mountain valleys, curving and branching, often streaked with dark lines of rock debris called moraines that trace their flow. Where a glacier's path is confined by a valley, the resemblance to a frozen river is unmistakable."
    },
    {
      "type": "heading",
      "text": "Ice Caps and Ice Sheets: Ice That Buries the Land"
    },
    {
      "type": "paragraph",
      "text": "Scale up a glacier until it stops following valleys and simply buries the entire landscape, and you have an ice cap or, on the largest scale, an ice sheet. These are vast domes of ice thick enough to drown whole mountain ranges, flowing outward in all directions from their high centres. Earth has two true ice sheets today, one covering almost all of Greenland and a far larger one over Antarctica. From orbit they appear as enormous, smooth white expanses with few features, so big they define the shape of the continents beneath them."
    },
    {
      "type": "heading",
      "text": "Sea Ice: Frozen Ocean"
    },
    {
      "type": "paragraph",
      "text": "Sea ice is something else entirely: it is frozen seawater, floating on the surface of the ocean rather than resting on land. It forms and melts with the seasons, expanding across the polar seas in winter and shrinking back in summer. Because it floats and is pushed around by wind and currents, sea ice tends to look broken, cracked, and mobile from above, a shifting mosaic of plates and open channels rather than a solid sheet. The frozen Arctic Ocean and the ring of ice around Antarctica are sea ice, not land ice."
    },
    {
      "type": "heading",
      "text": "Ice Shelves: Where Land Ice Meets the Sea"
    },
    {
      "type": "paragraph",
      "text": "At the edges of the great ice sheets, glaciers and ice flowing off the land can push out over the ocean and float while still attached to the ice behind them. These floating extensions are called ice shelves, and they form some of the most dramatic boundaries on Earth: sheer ice cliffs hundreds of metres tall facing the sea. From space, ice shelves appear as smooth white aprons fringing the coast of Antarctica, with long cracks where huge tabular icebergs will eventually break away."
    },
    {
      "type": "heading",
      "text": "Telling Them Apart From Above"
    },
    {
      "type": "paragraph",
      "text": "Put it together and a few visual rules emerge. If the ice flows in narrow, branching channels down through mountains, it is a glacier. If it is a vast, smooth, near-featureless dome covering a whole landmass, it is an ice sheet or cap. If it looks broken into drifting plates over open water, it is sea ice. And if it is a smooth white shelf meeting the ocean in a straight cliff edge, it is an ice shelf. Texture and setting are everything: ice on land flows and is streaked; ice on the sea floats and fractures."
    },
    {
      "type": "heading",
      "text": "Why the Difference Matters"
    },
    {
      "type": "paragraph",
      "text": "The distinction is not just academic. Land ice melting into the sea, glaciers and ice sheets, adds water to the ocean and raises sea levels worldwide. Sea ice melting does not raise sea level much, since it was already floating, but it dramatically changes how much sunlight the planet reflects, because dark open water absorbs far more heat than bright ice. Watching how each kind of ice grows and shrinks from year to year is one of the clearest ways satellites track the health of the planet."
    }
  ]
};
